import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ReportShell } from "@/features/reports/ReportShell";
import { apiList } from "@/integrations/api/client";
import { useBranch } from "@/context/BranchContext";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { isBranchLocked } from "@/lib/roles";
import type { ReportFilters } from "@/features/reports/types";

export const Route = createFileRoute("/_authenticated/reports/po-register")({
  component: PoRegister,
});

type Row = {
  id: string;
  po_number: string;
  po_date: string;
  status: string;
  subtotal: number | null;
  tax_amount: number | null;
  total_amount: number | null;
  created_at: string;
  created_by: string | null;
  vendor: { name: string } | null;
  branch: { id: string; name: string } | null;
  department: { name: string } | null;
  items: Array<{ quantity_ordered: number; received_quantity: number }>;
};

function usePoRegister(filters: ReportFilters, effectiveBranch: string | null) {
  return useQuery({
    queryKey: ["report-po-register", filters, effectiveBranch],
    queryFn: async () => {
      const params: Record<string, any> = { limit: 1000 };
      if (effectiveBranch) params.branch = effectiveBranch;
      if (filters.vendorId) params.vendor = filters.vendorId;
      if (filters.departmentId) params.department = filters.departmentId;
      if (filters.status) params.status = filters.status;
      if (filters.dateFrom) params.po_date__gte = filters.dateFrom;
      if (filters.dateTo) params.po_date__lte = filters.dateTo;
      if (filters.search?.trim()) params.search = filters.search.trim();
      
      const res = await apiList<any>("/api/v1/procurement/purchase-orders/", params);
      
      // Map API response to the expected Row structure
      const rows = (res.results || []).map((po: any) => ({
        id: po.id,
        po_number: po.po_number || po.po_no,
        po_date: po.po_date,
        status: po.status,
        subtotal: po.subtotal || po.total_amount,
        tax_amount: po.tax_amount || 0,
        total_amount: po.total_amount,
        created_at: po.created_at,
        created_by: po.created_by,
        vendor: po.vendor ? { name: po.vendor_name || "Vendor" } : null,
        branch: po.branch ? { id: po.branch, name: "Branch" } : null,
        department: po.department ? { name: "Department" } : null,
        items: po.items || []
      }));
      return rows as Row[];
    },
  });
}

function PoRegister() {
  const [filters, setFilters] = useState<ReportFilters>({});
  const { currentBranchId } = useBranch();
  const { data: me } = useCurrentUser();
  const locked = isBranchLocked(me?.roles) ? (me?.profile?.branch_id ?? null) : null;
  const effectiveBranch = locked ?? filters.branchId ?? currentBranchId ?? null;
  const { data: rows = [], isLoading } = usePoRegister(filters, effectiveBranch);

  const shaped = useMemo(
    () =>
      rows.map((r) => {
        const ordered = r.items.reduce((s, it) => s + Number(it.quantity_ordered || 0), 0);
        const received = r.items.reduce((s, it) => s + Number(it.received_quantity || 0), 0);
        return {
          id: r.id,
          po_number: r.po_number,
          po_date: r.po_date,
          vendor: r.vendor?.name ?? "—",
          branch: r.branch?.name ?? "—",
          department: r.department?.name ?? "—",
          lines: r.items.length,
          ordered,
          received,
          pending: Math.max(0, ordered - received),
          subtotal: Number(r.subtotal ?? 0),
          tax: Number(r.tax_amount ?? 0),
          total: Number(r.total_amount ?? 0),
          status: r.status,
          created_at: r.created_at,
        };
      }),
    [rows],
  );

  const exportRows = () =>
    shaped.map((r) => ({
      "PO Number": r.po_number,
      "PO Date": r.po_date,
      Vendor: r.vendor,
      Branch: r.branch,
      Department: r.department,
      "Line Items": r.lines,
      "Qty Ordered": r.ordered,
      "Qty Received": r.received,
      "Qty Pending": r.pending,
      Subtotal: r.subtotal.toFixed(2),
      "Tax Amount": r.tax.toFixed(2),
      "Grand Total": r.total.toFixed(2),
      Status: r.status,
      "Created At": r.created_at,
    }));

  return (
    <ReportShell
      id="R-42"
      title="Purchase Order Register"
      description="Purchase orders with per-line aggregates, received vs pending quantities and totals."
      filters={filters}
      onChange={setFilters}
      enabled={["branchId", "departmentId", "vendorId", "status", "dateFrom", "dateTo", "search"]}
      exportRows={exportRows}
    >
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="grid place-items-center py-10">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>PO #</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Vendor</TableHead>
                  <TableHead>Branch</TableHead>
                  <TableHead>Dept</TableHead>
                  <TableHead className="text-right">Lines</TableHead>
                  <TableHead className="text-right">Ordered</TableHead>
                  <TableHead className="text-right">Received</TableHead>
                  <TableHead className="text-right">Pending</TableHead>
                  <TableHead className="text-right">Subtotal</TableHead>
                  <TableHead className="text-right">Tax</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {shaped.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="font-mono text-xs">
                      <Link to="/purchase-orders/$poId" params={{ poId: r.id }} className="text-primary hover:underline">
                        {r.po_number}
                      </Link>
                    </TableCell>
                    <TableCell>{r.po_date}</TableCell>
                    <TableCell>{r.vendor}</TableCell>
                    <TableCell>{r.branch}</TableCell>
                    <TableCell>{r.department}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.lines}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.ordered}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.received}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.pending}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.subtotal.toFixed(2)}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.tax.toFixed(2)}</TableCell>
                    <TableCell className="text-right tabular-nums font-semibold">{r.total.toFixed(2)}</TableCell>
                    <TableCell>
                      <Badge variant="secondary">{r.status}</Badge>
                    </TableCell>
                  </TableRow>
                ))}
                {shaped.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={13} className="py-8 text-center text-sm text-muted-foreground">
                      No purchase orders match these filters.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
          <div className="border-t p-3 text-xs text-muted-foreground">
            Showing {shaped.length} purchase orders (first 1000).
          </div>
        </CardContent>
      </Card>
    </ReportShell>
  );
}
